import { ClipboardIcon } from '@radix-ui/react-icons';
import { FaWhatsapp } from 'react-icons/fa';
import { ILead } from '@clickersphere/core/entities/ILead';
import { Button } from '../../../Button';
import EmptyState from '../../../EmptyState';
import Loader from '../../../Loader';
import Select from '../../../Select';
import { useMessageComposerController } from './useMessageComposerController';

interface IMessageComposerProps {
  lead: ILead;
}

export default function MessageComposer({ lead }: IMessageComposerProps) {
  const {
    t,
    templateOptions,
    selectedTemplateId,
    setSelectedTemplateId,
    renderedMessage,
    hasTemplates,
    isMessageTemplatesLoading,
    canSendWhatsApp,
    hasPhone,
    handleCopy,
    handleOpenWhatsApp,
    handleClose,
  } = useMessageComposerController(lead);

  return (
    <div className="my-4 flex w-full flex-col gap-4">
      {isMessageTemplatesLoading && (
        <div className="flex justify-center py-4">
          <Loader className="h-6 w-6" />
        </div>
      )}

      {!isMessageTemplatesLoading && !hasTemplates && (
        <EmptyState>
          <p>{t('lead.messageComposer.empty')}</p>
          <small>{t('lead.messageComposer.emptyHint')}</small>
        </EmptyState>
      )}

      {!isMessageTemplatesLoading && hasTemplates && (
        <>
          <Select
            placeholder={t('lead.messageComposer.selectPlaceholder')}
            options={templateOptions}
            value={selectedTemplateId}
            onChange={setSelectedTemplateId}
          />

          {renderedMessage && (
            <div className="flex flex-col gap-1">
              <span className="text-text-tertiary text-xs">
                {t('lead.messageComposer.preview')}
              </span>
              <p className="bg-surface-input border-border-input text-text-primary max-h-60 overflow-y-auto rounded border p-3 text-sm whitespace-pre-wrap">
                {renderedMessage}
              </p>
            </div>
          )}

          {!hasPhone && (
            <p className="text-text-tertiary text-xs">
              {t('lead.messageComposer.noPhone')}
            </p>
          )}

          <section className="mt-2 flex flex-col gap-2 sm:flex-row sm:gap-4">
            <Button
              color="ghost"
              disabled={!renderedMessage}
              onClick={handleCopy}
              className="w-full sm:w-auto"
            >
              <span className="flex items-center gap-2">
                <ClipboardIcon className="h-4 w-4" />
                {t('lead.messageComposer.copy')}
              </span>
            </Button>
            <Button
              disabled={!renderedMessage || !canSendWhatsApp}
              onClick={handleOpenWhatsApp}
              className="w-full sm:flex-1"
            >
              <span className="flex items-center gap-2">
                <FaWhatsapp className="h-4 w-4" />
                {t('lead.messageComposer.openWhatsApp')}
              </span>
            </Button>
          </section>
        </>
      )}

      <div className="flex justify-end">
        <Button
          color="ghost"
          labelPath="lead.messageComposer.close"
          className="w-full sm:w-40"
          onClick={handleClose}
        />
      </div>
    </div>
  );
}
